import type { MeasureSeries } from '@/apis/telemetry';
import { isOutsideBand } from './tripMath';

interface TemperatureLineProps {
  readings: MeasureSeries;
  /** Allowed range in °C; the shaded band. */
  band: { min: number | null; max: number | null } | null;
  /** Epoch ms of the left and right edges (the last few hours). */
  from: number;
  to: number;
}

const WIDTH = 600;
const HEIGHT = 80;

/**
 * The last few hours of temperature with the allowed range shaded. A line
 * drifting toward the edge of the band is what the room needs to see.
 */
const TemperatureLine = ({
  readings,
  band,
  from,
  to,
}: TemperatureLineProps) => {
  const points = readings.t
    .map((t, i) => [t, readings.v[i]] as const)
    .filter(([t, v]) => t >= from && t <= to && Number.isFinite(v));
  if (points.length === 0) return null;

  const values = points.map(([, v]) => v);
  const limits = [band?.min, band?.max].filter(
    (v): v is number => typeof v === 'number'
  );
  const low = Math.min(...values, ...limits) - 1;
  const high = Math.max(...values, ...limits) + 1;
  const x = (t: number) => ((t - from) / (to - from)) * WIDTH;
  const y = (v: number) => HEIGHT - ((v - low) / (high - low)) * HEIGHT;

  const latest = points[points.length - 1][1];
  const outside = isOutsideBand(latest, band);
  const path = points
    .map(([t, v], i) => `${i ? 'L' : 'M'}${x(t).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');
  const bandTop = y(band?.max ?? high);
  const bandBottom = y(band?.min ?? low);
  const [lastT, lastV] = points[points.length - 1];

  return (
    <div className='monitor-temperature'>
      <div className='monitor-temperature-now'>
        <span className='monitor-trip-label'>Temperature now</span>
        <span
          className={`monitor-temperature-value${outside ? ' monitor-temperature-value--out' : ''}`}
        >
          {latest.toFixed(1)} °C
        </span>
        {band && (
          <span className='monitor-trip-sub'>
            allowed {band.min ?? '–'} to {band.max ?? '–'} °C
          </span>
        )}
      </div>
      <svg
        className='monitor-temperature-chart'
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio='none'
        role='img'
        aria-label={`Temperature over the last hours, now ${latest.toFixed(1)} °C`}
      >
        {band && (
          <rect
            className='monitor-temperature-band'
            x={0}
            y={bandTop}
            width={WIDTH}
            height={Math.max(0, bandBottom - bandTop)}
          />
        )}
        <path
          className={`monitor-temperature-path${outside ? ' monitor-temperature-path--out' : ''}`}
          d={path}
          fill='none'
          vectorEffect='non-scaling-stroke'
        />
        <circle
          className={`monitor-temperature-dot${outside ? ' monitor-temperature-dot--out' : ''}`}
          cx={x(lastT)}
          cy={y(lastV)}
          r={4}
        />
      </svg>
    </div>
  );
};

export default TemperatureLine;
